import { z } from 'zod'
import { capitalize, uncapitalize } from './text'
import type { NamedProperties } from '@jotta/types/TypeUtils'

export type CreateEnumResult<
  Name extends string,
  K extends string,
  T extends [K, ...K[]],
> = NamedProperties<
  Name,
  {
    Name: z.ZodEnum<T>
    NameSchema: z.ZodEnum<T>
    isName: (val?: any) => val is z.infer<z.ZodEnum<T>>
    getName: (
      val?: any,
      defaultValue?: z.infer<z.ZodEnum<T>>,
    ) => z.infer<z.ZodEnum<T>> | typeof defaultValue
    Names: readonly K[]
  }
>

export function createEnum<
  Name extends string,
  K extends string,
  T extends [K, ...K[]],
>(name: Name, value: T, defaultGetValue?: T[number]) {
  const schema = z.enum(value)
  const result = {
    [name]: schema,
    [`${uncapitalize(name)}Schema`]: schema,
    [`is${capitalize(name)}`]: (val: any): val is K =>
      schema.safeParse(val).success,
    [`get${capitalize(name)}`]: (val: any, defaultValue: K) =>
      schema.safeParse(val).success ? val : defaultValue || defaultGetValue,
    [`${uncapitalize(name)}s`]: schema.options,
  } as CreateEnumResult<Name, K, T>
  return result
}
